import React, { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { ChevronLeft, ChevronRight } from "lucide-react";
import Button from "../common/ui/button/Button";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Use the worker URL via import.meta.url
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url
).href;

interface PdfPreviewProps {
  fileUrl: string;
  fileName: string;
}

export const PdfPreview: React.FC<PdfPreviewProps> = ({
  fileUrl,
  fileName: _fileName,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState<string | null>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setError(null);
  }

  function onDocumentLoadError(error: Error) {
    setError(`Failed to load PDF: ${error.message}`);
  }

  function goToPrevPage() {
    setPageNumber((page) => Math.max(1, page - 1));
  }

  function goToNextPage() {
    setPageNumber((page) => Math.min(numPages || 1, page + 1));
  }

  if (error) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center p-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[400px] flex-col bg-gray-50 dark:bg-gray-950">
      {/* PDF Navigation Controls */}
      {numPages && numPages > 1 && (
        <div className="flex items-center justify-center gap-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            startIcon={<ChevronLeft className="h-4 w-4" />}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Page {pageNumber} of {numPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            endIcon={<ChevronRight className="h-4 w-4" />}
          >
            Next
          </Button>
        </div>
      )}

      {/* PDF Document */}
      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center p-8">
              <div className="text-gray-600 dark:text-gray-400">
                Loading PDF...
              </div>
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            className="shadow-lg"
          />
        </Document>
      </div>
    </div>
  );
};
